/**
 * @fileoverview Pending letterhead extraction state
 *
 * Shared state machine behind the drag-to-select crop editor: render a PDF's
 * first page, auto-crop the zones, re-crop as trims change, and clean up the
 * blob URLs + render canvas when done. Used by the "Add new letterhead"
 * upload card and by the "Edit crop" flow on existing letterhead cards.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { extractErrorMessage } from "@/utils/errors";
import {
  DEFAULT_GAP_FRACTION,
  computeAutoCropGeometry,
  cropLetterheadFromPage,
  cropTrimKey,
  releaseRenderedPage,
  renderPdfPage,
  trimsForSelection,
  type CropTrims,
  type CropZone,
  type SelectionFraction,
  type SourceBodyMargins,
  type TrimEdge,
} from "@/utils/branding/extract-letterhead-images";
import type { PendingExtraction } from "./PendingExtractionPreview";

/** Saved state to reopen the editor with (from the assets' sourceSelection). */
export interface ExtractionSeed {
  selection?: Partial<Record<CropZone, SelectionFraction | null | undefined>>;
  flowBeside?: boolean;
  /** False when the saved header/footer opted out of page fit. */
  pageFit?: boolean;
  /**
   * Body margins saved with the letterhead. Undefined lets the editor
   * measure them from the page again.
   */
  bodyMargins?: SourceBodyMargins | null;
}

function revokeBlobUrls(p: PendingExtraction) {
  if (p.headerBlobUrl) URL.revokeObjectURL(p.headerBlobUrl);
  if (p.footerBlobUrl) URL.revokeObjectURL(p.footerBlobUrl);
  if (p.leftSideBlobUrl) URL.revokeObjectURL(p.leftSideBlobUrl);
  if (p.rightSideBlobUrl) URL.revokeObjectURL(p.rightSideBlobUrl);
}

function trimsOf(p: PendingExtraction): CropTrims {
  return {
    headerTrimPx: p.headerTrimPx,
    headerTopTrimPx: p.headerTopTrimPx,
    headerLeftTrimPx: p.headerLeftTrimPx,
    headerRightTrimPx: p.headerRightTrimPx,
    footerTrimPx: p.footerTrimPx,
    footerBottomTrimPx: p.footerBottomTrimPx,
    footerLeftTrimPx: p.footerLeftTrimPx,
    footerRightTrimPx: p.footerRightTrimPx,
    leftTrimPx: p.leftTrimPx,
    rightTrimPx: p.rightTrimPx,
    leftTopTrimPx: p.leftTopTrimPx ?? 0,
    leftBottomTrimPx: p.leftBottomTrimPx ?? 0,
    rightTopTrimPx: p.rightTopTrimPx ?? 0,
    rightBottomTrimPx: p.rightBottomTrimPx ?? 0,
  };
}

export function usePendingExtraction() {
  const [pending, setPending] = useState<PendingExtraction | null>(null);
  const [rendering, setRendering] = useState(false);
  const pendingRef = useRef<PendingExtraction | null>(null);
  pendingRef.current = pending;

  const cleanup = useCallback(() => {
    setPending((prev) => {
      if (prev) {
        revokeBlobUrls(prev);
        // Hand the multi-megapixel render canvas back immediately — without
        // this, processing several letterheads in a row exhausts the canvas
        // memory budget and later renders fail.
        releaseRenderedPage(prev.renderedPage);
      }
      return null;
    });
  }, []);

  // Release everything if the owner unmounts mid-edit.
  useEffect(
    () => () => {
      const p = pendingRef.current;
      if (p) {
        revokeBlobUrls(p);
        releaseRenderedPage(p.renderedPage);
      }
    },
    [],
  );

  /**
   * Render + auto-crop a PDF. With a `seed`, the saved selection is applied
   * on top of auto-detect so the editor reopens where the user left it.
   * Resolves true on success.
   */
  const loadFile = useCallback(
    async (file: File, seed?: ExtractionSeed): Promise<boolean> => {
      setRendering(true);
      try {
        const renderedPage = await renderPdfPage(file);
        const zero: CropTrims = {
          headerTrimPx: 0,
          headerTopTrimPx: 0,
          headerLeftTrimPx: 0,
          headerRightTrimPx: 0,
          footerTrimPx: 0,
          footerBottomTrimPx: 0,
          footerLeftTrimPx: 0,
          footerRightTrimPx: 0,
          leftTrimPx: 0,
          rightTrimPx: 0,
        };
        const seeded = seed?.selection
          ? trimsForSelection(
              computeAutoCropGeometry(renderedPage, DEFAULT_GAP_FRACTION),
              seed.selection,
            )
          : {};
        const trims: CropTrims = { ...zero, ...seeded };
        const flowBeside = !!seed?.flowBeside;
        const result = await cropLetterheadFromPage(
          renderedPage,
          DEFAULT_GAP_FRACTION,
          trims,
          { headerTight: flowBeside },
        );
        const bodyMarginsEdited = seed?.bodyMargins !== undefined;
        setPending({
          sourceFile: file,
          renderedPage,
          ...trims,
          headerBlobUrl: result.headerBlobUrl,
          footerBlobUrl: result.footerBlobUrl,
          leftSideBlobUrl: result.leftSideBlobUrl,
          rightSideBlobUrl: result.rightSideBlobUrl,
          headerImageFile: result.headerImageFile,
          footerImageFile: result.footerImageFile,
          leftSideImageFile: result.leftSideImageFile,
          rightSideImageFile: result.rightSideImageFile,
          selection: result.selection,
          mirrorSides: false,
          flowBeside,
          pageFit: seed?.pageFit !== false,
          bodyMargins: bodyMarginsEdited
            ? seed.bodyMargins
            : result.sourceBodyMargins,
          detectedBodyMargins: result.sourceBodyMargins,
          bodyMarginsEdited,
          disabledZones: {},
        });
        return true;
      } catch (err) {
        console.error("[Letterhead] Render failed", err);
        toast.error("We couldn't read that PDF.", {
          description: extractErrorMessage(
            err,
            "Please try a different file, or reload the page and try again.",
          ),
        });
        return false;
      } finally {
        setRendering(false);
      }
    },
    [],
  );

  /**
   * Apply a trim patch (one edge, or several for a whole-zone move), then
   * re-crop. Mirrors left↔right side trims when the toggle is on.
   */
  const recrop = useCallback(
    (patch: Partial<CropTrims>, flowBesideOverride?: boolean) => {
      setPending((prev) => {
        if (!prev) return prev;
        revokeBlobUrls(prev);
        const flowBeside = flowBesideOverride ?? !!prev.flowBeside;
        const nextTrims: CropTrims = { ...trimsOf(prev), ...patch };
        if (prev.mirrorSides) {
          if (patch.leftTrimPx !== undefined && patch.rightTrimPx === undefined)
            nextTrims.rightTrimPx = patch.leftTrimPx;
          if (patch.rightTrimPx !== undefined && patch.leftTrimPx === undefined)
            nextTrims.leftTrimPx = patch.rightTrimPx;
        }
        // The state below carries the new trims immediately; a follow-up
        // setState swaps in the fresh crops once the async re-crop completes.
        void cropLetterheadFromPage(
          prev.renderedPage,
          DEFAULT_GAP_FRACTION,
          nextTrims,
          { headerTight: flowBeside },
        ).then((result) => {
          setPending((p) =>
            p
              ? {
                  ...p,
                  ...nextTrims,
                  headerBlobUrl: result.headerBlobUrl,
                  footerBlobUrl: result.footerBlobUrl,
                  leftSideBlobUrl: result.leftSideBlobUrl,
                  rightSideBlobUrl: result.rightSideBlobUrl,
                  headerImageFile: result.headerImageFile,
                  footerImageFile: result.footerImageFile,
                  leftSideImageFile: result.leftSideImageFile,
                  rightSideImageFile: result.rightSideImageFile,
                  selection: result.selection,
                  detectedBodyMargins: result.sourceBodyMargins,
                  // Follow the crop until the user types their own margins.
                  bodyMargins: p.bodyMarginsEdited
                    ? p.bodyMargins
                    : result.sourceBodyMargins,
                }
              : p,
          );
        });
        return { ...prev, ...nextTrims, flowBeside };
      });
    },
    [],
  );

  const applyTrims = useCallback(
    (patch: Partial<CropTrims>) => recrop(patch),
    [recrop],
  );

  const setFlowBeside = useCallback(
    (next: boolean) => recrop({}, next),
    [recrop],
  );

  const handleTrimChange = useCallback(
    (zone: CropZone, edge: TrimEdge, trimPx: number) =>
      applyTrims({ [cropTrimKey(zone, edge)]: trimPx }),
    [applyTrims],
  );

  const setPageFit = useCallback((next: boolean) => {
    setPending((p) => (p ? { ...p, pageFit: next } : p));
  }, []);

  /** The user's own body margins (null = keep the firm's margins). */
  const setBodyMargins = useCallback((next: SourceBodyMargins | null) => {
    setPending((p) =>
      p ? { ...p, bodyMargins: next, bodyMarginsEdited: true } : p,
    );
  }, []);

  /** Back to whatever the crop measured from the page. */
  const resetBodyMargins = useCallback(() => {
    setPending((p) =>
      p
        ? {
            ...p,
            bodyMargins: p.detectedBodyMargins ?? null,
            bodyMarginsEdited: false,
          }
        : p,
    );
  }, []);

  const setMirrorSides = useCallback((next: boolean) => {
    setPending((p) => (p ? { ...p, mirrorSides: next } : p));
  }, []);

  const toggleZone = useCallback((zone: CropZone, enabled: boolean) => {
    setPending((p) =>
      p
        ? {
            ...p,
            disabledZones: { ...(p.disabledZones ?? {}), [zone]: !enabled },
          }
        : p,
    );
  }, []);

  return {
    pending,
    rendering,
    loadFile,
    cleanup,
    applyTrims,
    handleTrimChange,
    setMirrorSides,
    setFlowBeside,
    setPageFit,
    setBodyMargins,
    resetBodyMargins,
    toggleZone,
  };
}

/** What a save writes for one letterhead zone. */
export interface ZoneSaveTarget {
  file: File;
  /** Selection on the source page, tagged with the editor zone it came from. */
  sourceSelection: SelectionFraction & { capturedAs: CropZone };
  sideFit?: "fullHeight" | "topBlock";
  topBlockHeightPct?: number;
  defaultPageRule?: "firstOnly";
  /** Header/footer only: page fit unless the user opted out. */
  imageFit?: "slot" | "page";
  /**
   * Header/footer only: the body column to save with page-fit artwork.
   * An empty object records that the user cleared both sides (so the
   * editor reopens blank instead of measuring again); null clears any
   * saved column (slot fit, or nothing was ever measured or typed);
   * absent for zones that never carry one.
   */
  sourceBodyMargins?: SourceBodyMargins | null;
}

/**
 * Resolve the pending crops into per-zone save targets, honouring zone
 * toggles and "text flows beside" (which saves the header as a topBlock
 * side asset on whichever side of the page it sits).
 */
export function zoneSaveTargets(
  p: PendingExtraction,
): Partial<Record<CropZone, ZoneSaveTarget>> {
  const files = enabledZoneFiles(p);
  const sel = p.selection;
  const out: Partial<Record<CropZone, ZoneSaveTarget>> = {};
  const tag = (zone: CropZone, s: SelectionFraction | null | undefined) =>
    s ? { ...s, capturedAs: zone } : null;
  const imageFit = p.pageFit === false ? "slot" : "page";
  const sourceBodyMargins =
    imageFit !== "page"
      ? null
      : (p.bodyMargins ?? (p.bodyMarginsEdited ? {} : null));

  if (files.header) {
    const s = tag("header", sel?.header);
    if (s && p.flowBeside) {
      const side: CropZone =
        (s.left + s.right) / 2 < 0.5 ? "leftSide" : "rightSide";
      out[side] = {
        file: files.header,
        sourceSelection: s,
        sideFit: "topBlock",
        topBlockHeightPct: Math.max(0.05, Math.min(0.9, s.bottom - s.top)),
        defaultPageRule: "firstOnly",
      };
    } else if (s) {
      out.header = {
        file: files.header,
        sourceSelection: s,
        imageFit,
        sourceBodyMargins,
      };
    }
  }
  if (files.footer) {
    const s = tag("footer", sel?.footer);
    if (s)
      out.footer = {
        file: files.footer,
        sourceSelection: s,
        imageFit,
        sourceBodyMargins,
      };
  }
  for (const zone of ["leftSide", "rightSide"] as const) {
    const f = files[zone];
    const s = tag(zone, sel?.[zone]);
    if (f && s && !out[zone]) {
      out[zone] = { file: f, sourceSelection: s, sideFit: "fullHeight" };
    }
  }
  return out;
}

/**
 * Rebuild an editor seed from saved assets (their `sourceSelection`), so
 * re-editing starts from the saved regions rather than a fresh auto-detect.
 * A saved body column (even an empty one, meaning the user cleared both
 * sides) is restored as-is; only assets with no saved column at all are
 * measured again.
 */
export function seedFromAssets(
  assets: Array<{
    zoneKind?: string | null;
    imageFit?: string | null;
    sourceSelection?:
      (SelectionFraction & { capturedAs?: CropZone | null }) | null;
    sourceBodyMargins?: SourceBodyMargins | null;
  }>,
): ExtractionSeed | undefined {
  const selection: NonNullable<ExtractionSeed["selection"]> = {};
  const { flowBeside, any } = assets.reduce(
    (acc, a) => {
      const s = a.sourceSelection;
      if (!s) return acc;
      const zone = (s.capturedAs ?? a.zoneKind) as CropZone | undefined;
      if (!zone) return acc;
      selection[zone] = {
        top: s.top,
        bottom: s.bottom,
        left: s.left,
        right: s.right,
      };
      return {
        flowBeside:
          acc.flowBeside || (zone === "header" && a.zoneKind !== "header"),
        any: true,
      };
    },
    { flowBeside: false, any: false },
  );
  // Only an explicit opt-out survives a re-edit: assets saved before page
  // fit existed carry no `imageFit`, and re-saving them adopts it.
  const pageFit = !assets.some(
    (a) =>
      (a.zoneKind === "header" || a.zoneKind === "footer") &&
      a.imageFit === "slot",
  );
  const saved = assets.find(
    (a) =>
      (a.zoneKind === "header" || a.zoneKind === "footer") &&
      a.sourceBodyMargins,
  )?.sourceBodyMargins;
  const bodyMargins = saved
    ? {
        ...(saved.left !== undefined ? { left: saved.left } : {}),
        ...(saved.right !== undefined ? { right: saved.right } : {}),
      }
    : undefined;
  return any ? { selection, flowBeside, pageFit, bodyMargins } : undefined;
}

/** The zone files to save, honoring the user's on/off toggles. */
export function enabledZoneFiles(p: PendingExtraction): {
  header: File | null;
  footer: File | null;
  leftSide: File | null;
  rightSide: File | null;
} {
  const off = p.disabledZones ?? {};
  return {
    header: off.header ? null : p.headerImageFile,
    footer: off.footer ? null : p.footerImageFile,
    leftSide: off.leftSide ? null : p.leftSideImageFile,
    rightSide: off.rightSide ? null : p.rightSideImageFile,
  };
}
